import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { api, type Paginated } from '@/lib/api';
import type { Enologist, GrapeVariety, Place, Producer, Wine, WineFilters, Winery } from './types';

export function useWines(filters: WineFilters = {}) {
  return useQuery({
    queryKey: ['wines', filters],
    queryFn: async () => {
      const { data } = await api.get<Paginated<Wine>>('/wines', { params: filters });
      return data;
    },
  });
}

export function useWine(id: number | undefined) {
  return useQuery({
    enabled: !!id,
    queryKey: ['wine', id],
    queryFn: async () => {
      const { data } = await api.get<{ data: Wine }>(`/wines/${id}`);
      return data.data;
    },
  });
}

export function useWineries() {
  return useQuery({
    queryKey: ['wineries'],
    queryFn: async () => {
      const { data } = await api.get<Paginated<Winery>>('/wineries', { params: { per_page: 200 } });
      return data.data;
    },
  });
}

export function useGrapeVarieties() {
  return useQuery({
    queryKey: ['grape-varieties'],
    queryFn: async () => {
      const { data } = await api.get<Paginated<GrapeVariety>>('/grape-varieties', { params: { per_page: 500 } });
      return data.data;
    },
  });
}

export function useProducers() {
  return useQuery({
    queryKey: ['producers'],
    queryFn: async () => {
      const { data } = await api.get<Paginated<Producer>>('/producers', { params: { per_page: 500 } });
      return data.data;
    },
  });
}

export function useEnologists() {
  return useQuery({
    queryKey: ['enologists'],
    queryFn: async () => {
      const { data } = await api.get<Paginated<Enologist>>('/enologists', { params: { per_page: 500 } });
      return data.data;
    },
  });
}

export function usePlaces() {
  return useQuery({
    queryKey: ['places'],
    queryFn: async () => {
      const { data } = await api.get<Paginated<Place>>('/places', { params: { per_page: 500 } });
      return data.data;
    },
  });
}

/** Solo países (type=country) — para el filtro de origen del listado. */
export function useCountries() {
  return useQuery({
    queryKey: ['places', 'countries'],
    queryFn: async () => {
      const { data } = await api.get<Paginated<Place>>('/places', {
        params: { type: 'country', per_page: 200 },
      });
      return data.data;
    },
  });
}

export function useSaveWine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }: { id?: number; payload: any }) => {
      if (id) {
        const { data } = await api.put<{ data: Wine }>(`/wines/${id}`, payload);
        return data.data;
      }
      const { data } = await api.post<{ data: Wine }>('/wines', payload);
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['wines'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

/** Toggle del check de inventario (balance) — legacy, ahora se usa por botella. */
export function useSetWineChecked() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, checked }: { id: number; checked: boolean }) => {
      const { data } = await api.patch<{ data: Wine }>(`/wines/${id}/check`, { checked });
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['wines'] });
    },
  });
}

/**
 * Confirma UNA botella pendiente del vino. Suma 1 a bottles_confirmed.
 */
export function useConfirmBottle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id }: { id: number }) => {
      const { data } = await api.post<{ data: any }>(`/wines/${id}/bottles/confirm`);
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['wines'] });
      qc.invalidateQueries({ queryKey: ['wine'] });
    },
  });
}

export type RemovalReason = 'consumption' | 'gift' | 'loss' | 'sale';

/**
 * Da de baja UNA botella del vino con razón. Decrementa el stock y registra
 * la salida.
 */
export function useRemoveBottle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: number; reason: RemovalReason }) => {
      const { data } = await api.post<{ data: any }>(`/wines/${id}/bottles/remove`, { reason });
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['wines'] });
      qc.invalidateQueries({ queryKey: ['wine'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

/**
 * Agrega N botellas al stock del vino. Crea un BottleLot (ajuste de stock,
 * sin compra asociada) y materializa las wine_bottles automáticamente.
 */
export function useAddBottles() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, quantity, notes }: { id: number; quantity: number; notes?: string }) => {
      const { data } = await api.post<{ data: any; lot_id: number }>(
        `/wines/${id}/bottles/add`,
        { quantity, notes },
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['wines'] });
      qc.invalidateQueries({ queryKey: ['wine'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

/**
 * Descuenta N botellas del stock como ajuste manual. Usar cuando se
 * registraron de más por error. No es consumo / regalo / merma.
 */
export function useDiscardBottles() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, quantity, notes }: { id: number; quantity: number; notes?: string }) => {
      const { data } = await api.post<{ data: any }>(`/wines/${id}/bottles/discard`, {
        quantity, notes,
      });
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['wines'] });
      qc.invalidateQueries({ queryKey: ['wine'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

/**
 * Reset masivo del balance — vuelve a 'pending' todas las botellas in_cellar
 * del tenant.
 */
export function useResetInventoryCheck() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post<{ reset_count: number }>('/inventory/bottles/reset');
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['wines'] });
    },
  });
}

/** Preview de cuántos vinos con stock 0 hay + sample de nombres. */
export function useEmptyStockPreview() {
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.get<{ count: number; sample: string[] }>(
        '/wines/empty-stock/count',
      );
      return data;
    },
  });
}

/** Bulk soft-delete: elimina todos los vinos del tenant con stock 0. */
export function useBulkDeleteEmpty() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post<{ deleted_count: number; deleted_ids: number[] }>(
        '/wines/bulk-delete-empty',
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['wines'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      qc.invalidateQueries({ queryKey: ['verticals'] });
    },
  });
}

/**
 * Elimina un vino (soft-delete). Útil para vinos mal cargados que no tienen
 * stock ni catas relevantes. Si el vino tiene mucha historia asociada, es
 * mejor usar el merge (useMergeWine).
 */
export function useDeleteWine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/wines/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['wines'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

/**
 * Une dos vinos duplicados. El vino "source" desaparece con soft-delete y
 * todo su stock, compras, catas, precios, botellas, cajas, imágenes y aliases
 * pasan al target.
 */
export function useMergeWine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ sourceId, targetId }: { sourceId: number; targetId: number }) => {
      const { data } = await api.post<{ data: Wine; moved: Record<string, number>; alias_added: boolean }>(
        `/wines/${sourceId}/merge`,
        { target_id: targetId },
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['wines'] });
      qc.invalidateQueries({ queryKey: ['wine'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      qc.invalidateQueries({ queryKey: ['boxes'] });
    },
  });
}

/**
 * Toggle rápido del flag is_keeper (Guarda / En venta).
 * No invalida el cache: actualizamos optimistically para que el listado
 * no se sacuda al cambiar el switch.
 */
export function useSetKeeper() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, isKeeper }: { id: number; isKeeper: boolean }) => {
      const { data } = await api.patch<{ data: Wine }>(`/wines/${id}/keeper`, {
        is_keeper: isKeeper,
      });
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['wines'] });
      qc.invalidateQueries({ queryKey: ['wine'] });
    },
  });
}
